import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../hooks/useAuth';
import Header from '../../../components/feature/Header';
import CommentForm from './components/CommentForm';
import CommentCard from './components/CommentCard';
import ShareModal from '../../../components/base/ShareModal';
import ReportModal from '../../../components/base/ReportModal';
import ShareToMessageModal, { SharedContent } from '../../../components/base/ShareToMessageModal';
import { encodeSharedContent } from '../../../components/base/SharedContentCard';
import { useMessages } from '../../../hooks/useMessages';
import VerifiedCreatorBadge from '../../../components/base/VerifiedCreatorBadge';

interface Post {
  id: string;
  title: string;
  content: string;
  content_html?: string;
  author_id: string;
  category_id: string;
  upvotes: number;
  downvotes: number;
  created_at: string;
  updated_at: string;
  tags?: string[];
  images?: string[];
  metadata?: any;
  user_profiles?: {
    username: string;
    avatar_url?: string;
    reputation: number;
    bio?: string;
    created_at: string;
    is_verified_creator?: boolean;
  } | null;
  discussion_categories?: {
    name: string;
    color: string;
    description?: string;
  } | null;
}

interface Comment {
  id: string;
  content: string;
  content_html?: string;
  author_id: string;
  post_id: string;
  parent_id?: string;
  upvotes: number;
  downvotes: number;
  created_at: string;
  user_profiles?: {
    username: string;
    avatar_url?: string;
    reputation: number;
    is_verified_creator?: boolean;
  } | null;
}

export default function PostDetailPage() {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [post, setPost] = useState<Post | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [voting, setVoting] = useState(false);
  const [userVote, setUserVote] = useState<'up' | 'down' | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [showShareToMessage, setShowShareToMessage] = useState(false);
  const [shareToMessageContent, setShareToMessageContent] = useState<SharedContent | null>(null);

  const { sendMessage, startConversation, fetchConversations } = useMessages();

  useEffect(() => {
    if (postId) {
      fetchPost();
      fetchComments();
      if (user) {
        checkUserVote();
        checkBookmark();
      }
    }
  }, [postId, user]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('posts')
        .select(`
          *,
          user_profiles (
            username,
            avatar_url,
            reputation,
            bio,
            created_at,
            is_verified_creator
          ),
          discussion_categories (
            name,
            color,
            description
          )
        `)
        .eq('id', postId)
        .single();

      if (error) throw error;
      setPost(data);
    } catch (error: any) {
      setError(error.message || 'Failed to load post');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      setCommentsLoading(true);
      const { data, error } = await supabase
        .from('comments')
        .select(`
          *,
          user_profiles (
            username,
            avatar_url,
            reputation,
            is_verified_creator
          )
        `)
        .eq('post_id', postId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setComments(data || []);
    } catch (error: any) {
      console.error('Error fetching comments:', error);
    } finally {
      setCommentsLoading(false);
    }
  };

  const checkUserVote = async () => {
    try {
      const { data } = await supabase
        .from('post_votes')
        .select('vote_type')
        .eq('post_id', postId)
        .eq('user_id', user?.id)
        .single();

      if (data) {
        setUserVote(data.vote_type);
      }
    } catch (error) {
      // No vote found
    }
  };

  const checkBookmark = async () => {
    try {
      const { data } = await supabase
        .from('user_activities')
        .select('id')
        .eq('user_id', user?.id)
        .eq('activity_type', 'bookmark')
        .eq('target_id', postId)
        .single();

      setBookmarked(!!data);
    } catch (error) {
      // No bookmark found
    }
  };

  const handleVote = async (voteType: 'up' | 'down') => {
    if (!user || voting || !post) return;

    try {
      setVoting(true);
      
      const { data: existingVote } = await supabase
        .from('post_votes')
        .select('vote_type')
        .eq('post_id', post.id)
        .eq('user_id', user.id)
        .single();

      if (existingVote) {
        if (existingVote.vote_type === voteType) {
          // Remove vote
          await supabase
            .from('post_votes')
            .delete()
            .eq('post_id', post.id)
            .eq('user_id', user.id);
          setUserVote(null);
        } else {
          // Update vote
          await supabase
            .from('post_votes')
            .update({ vote_type: voteType })
            .eq('post_id', post.id)
            .eq('user_id', user.id);
          setUserVote(voteType);
        }
      } else {
        // Create new vote
        await supabase
          .from('post_votes')
          .insert({
            post_id: post.id,
            user_id: user.id,
            vote_type: voteType
          });
        setUserVote(voteType);
      }

      // Refresh post to get updated vote counts
      fetchPost();
    } catch (error) {
      console.error('Error voting:', error);
    } finally {
      setVoting(false);
    }
  };

  const handleBookmark = async () => {
    if (!user || !post) return;

    try {
      if (bookmarked) {
        // Remove bookmark
        await supabase
          .from('user_activities')
          .delete()
          .eq('user_id', user.id)
          .eq('activity_type', 'bookmark')
          .eq('target_id', post.id);
        setBookmarked(false);
      } else {
        // Add bookmark
        await supabase
          .from('user_activities')
          .insert({
            user_id: user.id,
            activity_type: 'bookmark',
            target_type: 'post',
            target_id: post.id,
            metadata: {
              post_title: post.title,
              category: post.discussion_categories?.name
            }
          });
        setBookmarked(true);
      }
    } catch (error) {
      console.error('Error bookmarking:', error);
    }
  };

  const handleShareToMessage = () => {
    if (!post) return;
    setShareToMessageContent({
      type: 'post',
      id: post.id,
      title: post.title,
      description: post.content.substring(0, 120) + (post.content.length > 120 ? '...' : ''),
      image: post.user_profiles?.avatar_url || undefined,
      url: `/post/${post.id}`,
      meta: {
        category: post.discussion_categories?.name,
        categoryColor: post.discussion_categories?.color,
        author: post.user_profiles?.username
      }
    });
    setShowShareToMessage(true);
  };

  const handleSendSharedContent = async (conversationId: string, content: SharedContent) => {
    const encoded = encodeSharedContent(content);
    await sendMessage(conversationId, encoded);
  };

  const handleStartNewConvWithContent = async (userId: string, content: SharedContent) => {
    const convId = await startConversation(userId);
    if (convId) {
      const encoded = encodeSharedContent(content);
      await sendMessage(convId, encoded);
      await fetchConversations();
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeSince = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="animate-pulse">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="h-8 bg-gray-200 rounded mb-4"></div>
              <div className="h-4 bg-gray-200 rounded mb-2"></div>
              <div className="h-4 bg-gray-200 rounded mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <i className="ri-error-warning-line text-4xl text-red-500 mb-4"></i>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Post Not Found</h2>
            <p className="text-gray-600 mb-4">
              {error || 'The post you\'re looking for doesn\'t exist or has been removed.'}
            </p>
            <button
              onClick={() => navigate('/forum')}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Forum
            </button>
          </div>
        </div>
      </div>
    );
  }

  const netVotes = post.upvotes - post.downvotes;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="mb-6">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Link to="/forum" className="hover:text-blue-600 transition-colors">
              Forum
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            {post.discussion_categories && (
              <>
                <Link 
                  to={`/forum/${post.discussion_categories.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className="hover:text-blue-600 transition-colors"
                >
                  {post.discussion_categories.name}
                </Link>
                <i className="ri-arrow-right-s-line"></i>
              </>
            )}
            <span className="text-gray-900 truncate">{post.title}</span>
          </div>
        </nav>

        {/* Post Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          {/* Post Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                {post.discussion_categories && (
                  <span 
                    className="px-3 py-1 rounded-full text-sm font-medium text-white"
                    style={{ backgroundColor: post.discussion_categories.color }}
                  >
                    {post.discussion_categories.name}
                  </span>
                )}
                <span className="text-gray-500 text-sm">{getTimeSince(post.created_at)}</span>
                {post.updated_at !== post.created_at && (
                  <span className="text-gray-400 text-sm">(edited)</span>
                )}
              </div>

              <div className="relative">
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <i className="ri-more-line text-lg"></i>
                </button>
                
                {showMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                    <button
                      onClick={() => {
                        setShowShareModal(true);
                        setShowMenu(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                    >
                      <i className="ri-share-line mr-2"></i>
                      Share Post
                    </button>
                    {user && (
                      <button
                        onClick={() => {
                          handleBookmark();
                          setShowMenu(false);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                      >
                        <i className={`${bookmarked ? 'ri-bookmark-fill' : 'ri-bookmark-line'} mr-2`}></i>
                        {bookmarked ? 'Remove Bookmark' : 'Bookmark Post'}
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setShowReportModal(true);
                        setShowMenu(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center"
                    >
                      <i className="ri-flag-line mr-2"></i>
                      Report Post
                    </button>
                  </div>
                )}
              </div>
            </div>

            <h1 className="text-2xl font-bold text-gray-900 mb-4">{post.title}</h1>

            {/* Author Info */}
            <div className="flex items-center space-x-3">
              {post.user_profiles?.avatar_url ? (
                <img 
                  src={post.user_profiles.avatar_url} 
                  alt={post.user_profiles.username}
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center">
                  <i className="ri-user-line text-gray-600"></i>
                </div>
              )}
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">
                    {post.user_profiles?.username || 'Unknown User'}
                  </span>
                  {post.user_profiles?.is_verified_creator && (
                    <VerifiedCreatorBadge size="xs" />
                  )}
                  {post.user_profiles?.reputation !== undefined && (
                    <span className="text-sm text-gray-500">
                      ({post.user_profiles.reputation} pts)
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-500">
                  Member since {post.user_profiles?.created_at ? 
                    new Date(post.user_profiles.created_at).getFullYear() : 'Unknown'
                  }
                </div>
              </div>
            </div>
          </div>

          {/* Post Body */}
          <div className="p-6">
            <div className="flex gap-6">
              {/* Vote Section */}
              <div className="flex flex-col items-center gap-2 min-w-[60px]">
                <button
                  onClick={() => handleVote('up')}
                  disabled={!user || voting}
                  className={`p-2 rounded-full hover:bg-gray-100 transition-colors ${
                    userVote === 'up' ? 'text-green-600 bg-green-50' : 'text-gray-400'
                  } ${!user ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                >
                  <i className="ri-arrow-up-line text-2xl"></i>
                </button>
                <span className={`font-bold text-lg ${
                  netVotes > 0 ? 'text-green-600' : netVotes < 0 ? 'text-red-600' : 'text-gray-600'
                }`}>
                  {netVotes}
                </span>
                <button
                  onClick={() => handleVote('down')}
                  disabled={!user || voting}
                  className={`p-2 rounded-full hover:bg-gray-100 transition-colors ${
                    userVote === 'down' ? 'text-red-600 bg-red-50' : 'text-gray-400'
                  } ${!user ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                >
                  <i className="ri-arrow-down-line text-2xl"></i>
                </button>
              </div>

              {/* Content */}
              <div className="flex-1">
                <div className="prose max-w-none mb-6">
                  {post.content_html ? (
                    <div dangerouslySetInnerHTML={{ __html: post.content_html }} />
                  ) : (
                    <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                      {post.content}
                    </div>
                  )}
                </div>

                {/* Images */}
                {post.images && post.images.length > 0 && (
                  <div className="mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {post.images.map((image, index) => (
                        <img
                          key={index}
                          src={image}
                          alt={`Post image ${index + 1}`}
                          className="w-full h-48 object-cover rounded-lg border cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => window.open(image, '_blank')}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Tags */}
                {post.tags && post.tags.length > 0 && (
                  <div className="mb-6">
                    <div className="flex flex-wrap gap-2">
                      {post.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-block bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full hover:bg-blue-200 transition-colors cursor-pointer"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Post Stats */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div className="flex items-center space-x-6 text-sm text-gray-500">
                    <span className="flex items-center">
                      <i className="ri-eye-line mr-1"></i>
                      {Math.floor(Math.random() * 1000) + 100} views
                    </span>
                    <span className="flex items-center">
                      <i className="ri-chat-3-line mr-1"></i>
                      {comments.length} comments
                    </span>
                    <span>Posted {formatDate(post.created_at)}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={handleShareToMessage}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-purple-600 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-message-3-line"></i>
                      Send in Message
                    </button>
                    <button
                      onClick={() => setShowShareModal(true)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-share-line"></i>
                      Share
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Comments Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Comments ({comments.length})
            </h2>
            
            {user ? (
              <CommentForm 
                postId={post.id} 
                onCommentAdded={fetchComments}
              />
            ) : (
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-gray-600 mb-3">Please sign in to leave a comment</p>
                <button
                  onClick={() => {/* Open auth modal */}}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Sign In
                </button>
              </div>
            )}
          </div>

          <div className="p-6">
            {commentsLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="flex space-x-3">
                      <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                      <div className="flex-1">
                        <div className="h-4 bg-gray-200 rounded mb-2"></div>
                        <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : comments.length > 0 ? (
              <div className="space-y-6">
                {comments.map((comment) => (
                  <CommentCard 
                    key={comment.id} 
                    comment={comment}
                    onVote={fetchComments}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <i className="ri-chat-3-line text-4xl text-gray-300 mb-4"></i>
                <p className="text-gray-500">No comments yet. Be the first to comment!</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        title={post.title}
        url={window.location.href}
        onShareToMessage={() => {
          setShowShareModal(false);
          handleShareToMessage();
        }}
      />

      <ReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        contentType="post"
        contentId={post.id}
        reportedUserId={post.author_id}
      />

      <ShareToMessageModal
        isOpen={showShareToMessage}
        onClose={() => setShowShareToMessage(false)}
        content={shareToMessageContent}
        onSend={handleSendSharedContent}
        onStartNewConversation={handleStartNewConvWithContent}
      />
    </div>
  );
}
